import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  createFilterOptions,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useHierarchyEntities } from '@/hooks';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import {
  buildLocationLabel,
  computeCommonBreadcrumbPrefixLen,
  filterLeafLocationOptions,
  formatLocationOptionLabel,
} from '@/utils/locationHierarchy';

export interface ReturnToShelfTargetGroup {
  groupKey: string;
  assetName: string;
  quantity: number;
  missingBinIds: string[];
}

export interface ReturnBinTarget {
  binId: string;
  targetLocationId: string;
}

interface LocationOption {
  id: string;
  label: string;
  breadcrumb?: string | null;
  identifier?: string | null;
  nodeTypeName?: string | null;
}

export interface TargetLocationSelectionModalProps {
  open: boolean;
  groups: ReturnToShelfTargetGroup[];
  onCancel: () => void;
  onConfirm: (targets: ReturnBinTarget[]) => void;
}

export const TargetLocationSelectionModal: React.FC<TargetLocationSelectionModalProps> = ({
  open,
  groups,
  onCancel,
  onConfirm,
}) => {
  const locationQuery = useHierarchyEntities({
    dataSource: {
      endpoint: '/api/v1/location_hierarchy',
      order: 'breadcrumb.asc',
    },
    filters: {
      select: 'id,display_name,entity_identifier,breadcrumb,node_type_name',
    },
    enabled: open,
  });

  const locationOptions = useMemo<LocationOption[]>(() => {
    return (locationQuery.data ?? []).map((row) => {
      const label = buildLocationLabel(row);
      return {
        id: row.id,
        label,
        breadcrumb: row.breadcrumb ?? null,
        identifier: row.entity_identifier ?? null,
        nodeTypeName: row.node_type_name ?? null,
      };
    });
  }, [locationQuery.data]);

  // Only allow selecting leaf (most-child) locations.
  // This avoids confusing duplicates where both a parent and its child appear as separate options.
  const leafLocationOptions = useMemo<LocationOption[]>(() => {
    return filterLeafLocationOptions(locationOptions);
  }, [locationOptions]);

  const commonBreadcrumbPrefixLen = useMemo(() => {
    return computeCommonBreadcrumbPrefixLen(leafLocationOptions);
  }, [leafLocationOptions]);

  const filterOptions = useMemo(
    () =>
      createFilterOptions<LocationOption>({
        stringify: (option) => {
          const display = formatLocationOptionLabel(option, commonBreadcrumbPrefixLen);
          return [
            display,
            option.breadcrumb ?? '',
            option.label ?? '',
            option.identifier ?? '',
            option.nodeTypeName ?? '',
          ]
            .join(' ')
            .trim();
        },
      }),
    [commonBreadcrumbPrefixLen],
  );

  const locationOptionMap = useMemo(() => {
    const map = new Map<string, LocationOption>();
    for (const option of locationOptions) {
      map.set(option.id, option);
    }
    return map;
  }, [locationOptions]);

  const [selectionByGroup, setSelectionByGroup] = useState<Record<string, LocationOption | null>>(
    {},
  );

  useEffect(() => {
    if (!open) {
      setSelectionByGroup({});
      return;
    }

    setSelectionByGroup((previous) => {
      const next: Record<string, LocationOption | null> = {};
      for (const group of groups) {
        next[group.groupKey] = previous[group.groupKey] ?? null;
      }
      return next;
    });
  }, [open, groups]);

  useEffect(() => {
    setSelectionByGroup((previous) => {
      const next: Record<string, LocationOption | null> = {};
      for (const [groupKey, selection] of Object.entries(previous)) {
        if (selection) {
          const canonical = locationOptionMap.get(selection.id) ?? selection;
          const isLeaf = leafLocationOptions.some((option) => option.id === canonical.id);
          next[groupKey] = isLeaf ? canonical : null;
        } else {
          next[groupKey] = null;
        }
      }
      return next;
    });
  }, [locationOptionMap, leafLocationOptions]);

  const isValid = useMemo(() => {
    if (!groups.length) {
      return true;
    }
    return groups.every((group) => Boolean(selectionByGroup[group.groupKey]?.id));
  }, [groups, selectionByGroup]);

  const handleConfirm = () => {
    const targets: ReturnBinTarget[] = [];

    for (const group of groups) {
      const selected = selectionByGroup[group.groupKey];
      if (!selected?.id) {
        return;
      }
      for (const binId of group.missingBinIds) {
        targets.push({
          binId,
          targetLocationId: selected.id,
        });
      }
    }

    onConfirm(targets);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Select Target Locations for Return to Shelf</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Some return items have no location assigned. Choose a target shelf location so labels
            can be generated and scanning can work.
          </Alert>

          {groups.map((group) => {
            const selection = selectionByGroup[group.groupKey] ?? null;

            return (
              <Box
                key={group.groupKey}
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="subtitle1" fontWeight={600}>
                      {group.assetName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.missingBinIds.length} missing location • {group.quantity} total
                    </Typography>
                  </Stack>

                  <Autocomplete<LocationOption>
                    value={selection}
                    options={leafLocationOptions}
                    loading={locationQuery.isLoading}
                    disabled={locationQuery.isLoading || locationQuery.isError}
                    filterOptions={filterOptions}
                    getOptionLabel={(option) =>
                      formatLocationOptionLabel(option, commonBreadcrumbPrefixLen)
                    }
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_event, newValue) => {
                      const canonical = newValue
                        ? (locationOptionMap.get(newValue.id) ?? newValue)
                        : null;
                      setSelectionByGroup((previous) => ({
                        ...previous,
                        [group.groupKey]: canonical,
                      }));
                    }}
                    renderInput={({ InputProps, InputLabelProps, inputProps, ...params }) => (
                      <TextField
                        {...params}
                        label="Target Location"
                        placeholder="Search locations"
                        slotProps={{
                          inputLabel: InputLabelProps,
                          htmlInput: inputProps,
                          input: {
                            ...InputProps,
                            endAdornment: (
                              <>
                                {locationQuery.isLoading ? (
                                  <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {InputProps?.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </Stack>
              </Box>
            );
          })}

          {locationQuery.isError ? (
            <Alert severity="error">Unable to load locations. Please try again.</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <ViewerGuardedButton variant="contained" onClick={handleConfirm} disabled={!isValid}>
          Continue
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};
